import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Delay (ms) before opening a submenu on pointer hover — reduces accidental opens on desktop. */
const POINTER_ACTIVATE_DELAY = 150;
const DEACTIVATE_DELAY = 350;
/** Extra quiet time after the open animation before L1 click may close/navigate. */
const POST_OPEN_ANIMATION_CLICK_GUARD_MS = 250;

const ACTIVATION_TEXT_SELECTOR = '.menu-list__link-title';

/** @type {boolean} */
let pageShowListenerBound = false;

function isFinePointerDevice() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isActivationTextTarget(element) {
  if (!(element instanceof Element)) return false;
  return element.matches(ACTIVATION_TEXT_SELECTOR) || !!element.closest(ACTIVATION_TEXT_SELECTOR);
}

/**
 * @param {HTMLElement} menuItem
 * @returns {HTMLElement | null}
 */
function getActivationTextElement(menuItem) {
  const title = menuItem.querySelector(ACTIVATION_TEXT_SELECTOR);
  return title instanceof HTMLElement ? title : null;
}

/**
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isActivationTextHovered(menuItem) {
  const title = getActivationTextElement(menuItem);
  return title?.matches(':hover') ?? false;
}

/**
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isSubmenuHovered(menuItem) {
  const listItem = menuItem.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('.menu-list__submenu');
  return submenu?.matches(':hover') ?? false;
}

/**
 * @param {PointerEvent} event
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isPointerOverActivationText(event, menuItem) {
  const title = getActivationTextElement(menuItem);
  if (!title) return false;

  if (title.matches(':hover')) return true;

  if (event.clientX === 0 && event.clientY === 0) {
    return title.matches(':hover');
  }

  const hit = document.elementFromPoint(event.clientX, event.clientY);
  return hit instanceof Element && (hit === title || title.contains(hit));
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {number} */
  #openedAt = 0;

  /** True when the current open came from an explicit click (not hover). */
  #openedViaClick = false;

  connectedCallback() {
    super.connectedCallback();

    this.#bindPageShowListener();
    this.#resetMenuState();
    this.#bindOverflowReflowGuard();

    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal,
    });

    // Click-to-open/close (desktop fine pointer). Capture so we can stop <a> navigation on first click.
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    document.addEventListener('keydown', this.#onDocumentKeyDown, { signal });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#resetMenuStateIfNotIntentionallyOpen();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#resetMenuState();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * True when another desktop menu in the same header already has a submenu open.
   * @returns {boolean}
   */
  #anySiblingHeaderMenuExpanded() {
    const root = this.closest('header-component');
    if (!root) return false;
    for (const el of root.querySelectorAll('header-menu')) {
      if (el === this) continue;
      if (el.getAttribute('aria-expanded') === 'true') return true;
    }
    return false;
  }

  /**
   * Resets open submenu state (e.g. after navigation or bfcache restore).
   */
  reset() {
    this.#resetMenuState();
  }

  /**
   * Keeps the submenu open while the pointer moves from the activation text into the panel.
   */
  holdOpen = () => {
    this.#debouncedDeactivate.cancel();
  };

  /**
   * After hover-open: wait for the submenu open animation, then an extra 250ms,
   * before L1 click may close or navigate.
   * @returns {boolean}
   */
  #isPostHoverClickGuardActive() {
    if (this.#openedViaClick) return false;
    if (!this.#state.activeItem) return false;
    const guardMs = this.animationDelay + POST_OPEN_ANIMATION_CLICK_GUARD_MS;
    return performance.now() - this.#openedAt < guardMs;
  }

  /**
   * First click on a closed L1 with submenu → open immediately (no 150ms hover delay).
   * Second click on the same open <a> → allow navigation to the category URL.
   * Second click on an open <button> (Meer / parent_title) → close.
   * Click on the dim backdrop (::after) → close immediately.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!isFinePointerDevice()) return;
    if (!(event.target instanceof Element)) return;

    // Backdrop is `.header-menu::after` — clicks on it target this host element.
    if (event.target === this) {
      if (this.#state.activeItem) {
        event.preventDefault();
        this.#closeImmediate();
      }
      return;
    }

    // Ignore clicks that originated inside an open submenu panel (links/content).
    if (event.target.closest('.menu-list__submenu')) return;

    const moreHost = event.target.closest('[slot="more"]');
    if (moreHost && this.#state.activeItem && this.dataset.overflowExpanded === 'true') {
      if (this.#isPostHoverClickGuardActive()) {
        event.preventDefault();
        return;
      }
      // Second interaction on "Meer" closes overflow instead of following an overflow <a>.
      event.preventDefault();
      this.#closeImmediate();
      return;
    }

    const item = findMenuItem(event.target);
    if (!item || item.getAttribute('aria-haspopup') !== 'true') return;

    if (item === this.#state.activeItem) {
      // Hover just opened this item — ignore the trailing click (no close / no navigate).
      if (this.#isPostHoverClickGuardActive()) {
        event.preventDefault();
        return;
      }
      // Already open: <a> → navigate to category; <button> → close.
      if (item instanceof HTMLAnchorElement) return;
      event.preventDefault();
      this.#closeImmediate();
      return;
    }

    // Closed (or different) item: open now, don't navigate away.
    event.preventDefault();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#openItem(item, { viaClick: true });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#state.activeItem) return;

    event.preventDefault();
    this.#closeImmediate();
  };

  #bindPageShowListener() {
    if (pageShowListenerBound) return;
    pageShowListenerBound = true;

    window.addEventListener('pageshow', () => {
      document.querySelectorAll('header-menu').forEach((menu) => {
        if (menu instanceof HeaderMenu) menu.reset();
      });
    });
  }

  #bindOverflowReflowGuard() {
    const overflowList = this.refs.overflowMenu;
    if (!(overflowList instanceof HTMLElement)) return;

    const observer = new ResizeObserver(() => {
      this.#resetMenuStateIfNotIntentionallyOpen();
    });

    observer.observe(overflowList);
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  #resetMenuStateIfNotIntentionallyOpen() {
    const activeItem = this.#state.activeItem;
    if (!(activeItem instanceof HTMLElement)) {
      this.#resetMenuState();
      return;
    }

    if (!isActivationTextHovered(activeItem) && !isSubmenuHovered(activeItem) && !this.overflowHovered) {
      this.#resetMenuState();
    }
  }

  #resetMenuState() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    const item = this.#state.activeItem;
    this.#state.activeItem = null;
    this.#openedAt = 0;
    this.#openedViaClick = false;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    this.removeAttribute('data-animating');

    if (item instanceof HTMLElement) {
      item.ariaExpanded = 'false';
      item.removeAttribute('data-animating');
    }

    this.querySelectorAll('[ref="menuitem"][aria-expanded="true"]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.ariaExpanded = 'false';
        el.removeAttribute('data-animating');
      }
    });

    this.querySelectorAll('.menu-list__submenu').forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        submenu.setAttribute('hidden', '');
        submenu.setAttribute('inert', '');
      }
    });
  }

  /**
   * Opens the submenu only when the pointer enters the activation text.
   * @param {PointerEvent} event
   */
  activate = (event) => {
    if (!(event instanceof PointerEvent) || !event.isTrusted || !isFinePointerDevice()) return;
    if (!(event.target instanceof Element) || !isActivationTextTarget(event.target)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    const skipPointerDelay =
      this.#state.activeItem != null || this.#anySiblingHeaderMenuExpanded();

    if (skipPointerDelay) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    if (item.getAttribute('aria-haspopup') !== 'true') return;

    if (!isPointerOverActivationText(event, item)) return;

    this.#openItem(item, { viaClick: false });
  };

  /**
   * Open a top-level menuitem submenu (shared by hover + click).
   * @param {HTMLElement} item
   * @param {{ viaClick?: boolean }} [options]
   */
  #openItem(item, options = {}) {
    if (item.getAttribute('aria-haspopup') !== 'true') return;
    if (item === this.#state.activeItem) return;

    this.#debouncedDeactivate.cancel();
    this.removeAttribute('data-animating');

    this.#openedAt = performance.now();
    this.#openedViaClick = options.viaClick === true;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const listItem = item.closest('.menu-list__list-item');
    const slotName = listItem?.getAttribute('slot') ?? '';
    this.dataset.overflowExpanded = String(slotName === 'overflow' || slotName === 'more');

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu instanceof HTMLElement) {
        previousSubmenu.setAttribute('hidden', '');
        previousSubmenu.setAttribute('inert', '');
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    // Overflow/"Meer" path: no per-item submenu node — use the overflow panel.
    if (!submenu) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenu instanceof HTMLElement) {
      submenu.removeAttribute('hidden');
      submenu.removeAttribute('inert');
      this.#promoteLazyImagesIn(submenu);
    }
  }

  #debouncedActivateHandler = debounce(this.#activateHandler, POINTER_ACTIVATE_DELAY);

  /**
   * @param {PointerEvent} event
   */
  deactivate(event) {
    if (!(event instanceof PointerEvent)) return;

    this.#debouncedActivateHandler.cancel();

    if (!this.#state.activeItem) return;

    const item =
      event.target instanceof Element ? findMenuItem(event.target) ?? this.#state.activeItem : this.#state.activeItem;

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;

    if (!options.force) {
      if (this.overflowHovered) return;
      if (isSubmenuHovered(item)) return;
      if (isActivationTextHovered(item)) return;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.#openedAt = 0;
    this.#openedViaClick = false;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const submenu = findSubmenu(item);
    if (submenu instanceof HTMLElement) {
      submenu.removeAttribute('hidden');
      submenu.removeAttribute('inert');
    }

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu instanceof HTMLElement) {
        submenu.setAttribute('hidden', '');
        submenu.setAttribute('inert', '');
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Force-close without leave-grace / hover guards (backdrop, Escape, button toggle).
   */
  #closeImmediate() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, { force: true });
    this.#openedAt = 0;
    this.#openedViaClick = false;
  }

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Start loading lazy images inside a submenu only after it opens (better initial page weight).
   * @param {HTMLElement} root
   */
  #promoteLazyImagesIn(root) {
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.removeAttribute('loading');
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreListItem = element.closest('[slot="more"]');
  if (moreListItem instanceof HTMLElement) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreListItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const ancestor = element.closest('[ref="menuitem"]');
  if (ancestor instanceof HTMLElement) return ancestor;

  const descendant = element.querySelector('[ref="menuitem"]');
  return descendant instanceof HTMLElement ? descendant : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
